import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/* Plan copy mirrors the public pricing page ("Solo audits, Agency delivers");
 * nothing is listed here that the pricing page does not promise. Yearly is
 * 20% off twelve months of the monthly rate; the amounts match the Stripe
 * catalog created by backend/setup_stripe.py. */
const PLANS = [
  {
    id: "free", name: "Free",
    pricing: {
      monthly: { price: "$0", period: "", lookup: null },
      yearly: { price: "$0", period: "", lookup: null },
    },
    features: ["1 client, 3 sites", "Unlimited audits, full audit engine", "Authenticated pages, tracking, checklist", "Conformance reports and statements", "Single element re-scan"],
  },
  {
    id: "solo", name: "Solo",
    pricing: {
      monthly: { price: "$39", period: "per month", lookup: "solo_monthly" },
      yearly: { price: "$374.40", period: "per year", lookup: "solo_yearly", note: "20% off monthly" },
    },
    features: ["Unlimited clients and sites", "Everything in the audit engine", "Suggested code fixes on every rule", "Single element re-scan", "Neutral PDF reports and statements"],
  },
  {
    id: "agency", name: "Agency",
    pricing: {
      monthly: { price: "$99", period: "per month", lookup: "agency_monthly" },
      yearly: { price: "$950.40", period: "per year", lookup: "agency_yearly", note: "20% off monthly" },
    },
    positioning: "Built for selling accessibility as a recurring service.",
    features: ["Everything in Solo", "White label PDF branding", "Branded shareable report links", "1 seat today, 3 with invites"],
  },
];

const btnCls = "bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2";
const outlineBtnCls = "bg-white border border-gray-300 rounded-sm px-4 py-2 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

const fmtCents = (cents) => `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;

// order of the tiers, so moving to a cheaper plan reads "Downgrade"
const PLAN_RANK = { free: 0, solo: 1, agency: 2 };

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const STATUS_STYLE = {
  active: "text-[#2E7D32]",
  trialing: "text-[#16324F]",
  past_due: "text-[#C62828]",
  canceled: "text-ink-muted",
};

export default function Billing() {
  const [profile, setProfile] = useState(null);
  const [sub, setSub] = useState(undefined); // undefined = loading, null = unavailable
  const [busy, setBusy] = useState("");
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/me").then((r) => setProfile(r.data)).catch(() => toast.error("Could not load profile"));
    api.get("/billing/subscription").then((r) => setSub(r.data.subscription)).catch(() => setSub(null));
  }, []);

  const upgrade = async (lookup) => {
    setBusy(lookup);
    track("checkout_started", { lookup_key: lookup });
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy("");
      setConfirmPlan(null);
    }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal");
      setBusy("");
    }
  };

  if (!profile) {
    return (
      <div data-testid="billing-page">
        <p className="text-sm text-ink-muted font-mono" data-testid="billing-loading">Loading current plan...</p>
      </div>
    );
  }

  const plan = profile.plan || "free";
  const current = PLANS.find((p) => p.id === plan) || PLANS[0];
  const status = sub?.status || (plan === "free" ? null : "active");
  const periodEnd = sub?.current_period_end;
  // a scheduled cancellation keeps status "active"; depending on the Stripe
  // API version it shows up as cancel_at_period_end, cancel_at, or canceled_at
  const cancelScheduled = !!sub && status !== "canceled" &&
    !!(sub.cancel_at_period_end || sub.cancel_at || sub.canceled_at);
  const cancelDate = sub?.cancel_at || periodEnd;
  // the live subscription knows the real amount and interval (monthly or
  // yearly); the static plan table is only the fallback while it loads
  const currentPrice = sub?.amount
    ? `${fmtCents(sub.amount)} per ${sub.interval === "year" ? "year" : "month"}`
    : `${current.pricing.monthly.price}${current.pricing.monthly.period ? ` ${current.pricing.monthly.period}` : ""}`;

  return (
    <div data-testid="billing-page">
      <div className="bg-white border border-gray-200 rounded-sm p-5 max-w-2xl mb-8" data-testid="billing-summary-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-wide text-ink-muted mb-1">Current plan</p>
            <p className="font-heading text-xl font-bold" data-testid="current-plan">{current.name}</p>
            <p className="text-sm text-ink-muted mt-0.5">
              <span className="font-mono">{currentPrice}</span>{plan === "free" ? ", no billing" : ", billed through Stripe"}
            </p>
          </div>
          {status && (
            <p className={`text-xs font-mono uppercase tracking-wide ${cancelScheduled ? STATUS_STYLE.canceled : STATUS_STYLE[status] || "text-ink-muted"}`} data-testid="billing-status">
              {cancelScheduled ? "cancels at period end" : status.replace("_", " ")}
            </p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-ink-muted" data-testid="billing-renewal-line">
          {plan === "free" ? (
            <p>The Free plan has no billing. Upgrade below when you need more than one client or client delivery features.</p>
          ) : sub === undefined ? (
            <p className="font-mono">Loading subscription details...</p>
          ) : !sub ? (
            <p>Renewal details are available in the Stripe billing portal.</p>
          ) : status === "trialing" ? (
            <p>
              Trial ends {fmtDate(sub.trial_end)}
              {sub.trial_end ? ` (${daysLeft(sub.trial_end)} days left)` : ""}. Billing starts after the trial unless you cancel.
            </p>
          ) : status === "past_due" ? (
            <p className="flex items-start gap-2 text-[#C62828]" role="alert">
              <AlertTriangle size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" aria-hidden="true" />
              Your last payment failed. Update your payment method in the billing portal to keep access.
            </p>
          ) : status === "canceled" ? (
            <p>Your subscription is canceled.{periodEnd ? ` Access ends ${fmtDate(periodEnd)}.` : ""} Your data stays readable on the Free plan afterwards.</p>
          ) : cancelScheduled ? (
            <p>
              Your plan will not renew.{cancelDate ? ` You keep full access until ${fmtDate(cancelDate)},` : " You keep full access until the end of the paid period,"} then
              the workspace moves to Free. You can resume the subscription from the billing portal anytime before then.
            </p>
          ) : (
            <p>Renews {periodEnd ? `on ${fmtDate(periodEnd)}` : sub?.interval === "year" ? "yearly" : "monthly"}.</p>
          )}
        </div>

        {plan !== "free" && (
          <div className="mt-4">
            <button data-testid="billing-portal-btn" onClick={portal} disabled={!!busy}
              className={status === "past_due" ? btnCls : outlineBtnCls}>
              {busy === "portal" ? "Opening..." : status === "past_due" ? "Update payment method" : "Manage billing"}
            </button>
            <p className="text-xs text-ink-muted mt-2">
              Payment method, invoice history, and cancellation are handled securely in the Stripe billing portal.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3">
        <h2 className="font-heading text-base font-semibold">Compare plans</h2>
        <div className="inline-flex border border-gray-300 rounded-sm overflow-hidden" role="group" aria-label="Billing cycle" data-testid="billing-cycle-toggle">
          {["monthly", "yearly"].map((c) => (
            <button key={c} type="button" onClick={() => setCycle(c)} aria-pressed={cycle === c}
              data-testid={`billing-cycle-${c}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] ${cycle === c ? "bg-[#16324F] text-white" : "bg-white text-ink-muted hover:bg-gray-50"}`}>
              {c === "monthly" ? "Monthly" : "Yearly, 20% off"}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
        {PLANS.map((p) => {
          const pc = p.pricing[cycle];
          return (
            <div key={p.id} className={`bg-white border rounded-sm p-5 flex flex-col ${plan === p.id ? "border-[#16324F] border-2" : "border-gray-200"}`} data-testid={`plan-card-${p.id}`}>
              <h3 className="font-heading text-base font-semibold">{p.name}</h3>
              <p className="my-2">
                <span className="font-mono text-2xl">{pc.price}</span>
                {pc.period && <span className="text-sm text-ink-muted"> {pc.period}</span>}
                {pc.note && <span className="block text-xs text-[#2E7D32] mt-0.5">{pc.note}</span>}
              </p>
              {p.positioning && <p className="text-xs text-ink-muted mb-2">{p.positioning}</p>}
              <ul className="text-sm text-ink-muted space-y-1.5 mb-4">
                {p.features.map((f) => <li key={f} className="border-b border-gray-100 pb-1.5 last:border-0">{f}</li>)}
              </ul>
              <div className="mt-auto">
                {plan === p.id ? (
                  <p className="text-xs font-mono text-[#2E7D32]" data-testid={`plan-active-${p.id}`}>Active plan</p>
                ) : pc.lookup ? (
                  <button data-testid={`upgrade-${p.id}-btn`} onClick={() => setConfirmPlan(p)} disabled={!!busy}
                    className={`w-full ${btnCls}`}>
                    {PLAN_RANK[p.id] < PLAN_RANK[plan] ? "Downgrade" : "Upgrade"} to {p.name}
                  </button>
                ) : (
                  <p className="text-xs text-ink-muted">Downgrades happen automatically when a subscription ends.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-ink-muted mt-4 max-w-4xl">
        Prices in USD, billed through Stripe monthly or yearly; yearly is 20% off the monthly rate. No usage quotas on any plan. Cancel anytime from the billing portal, effective at the end of the current period.
      </p>

      <Dialog open={!!confirmPlan} onOpenChange={(open) => { if (!open && !busy) setConfirmPlan(null); }}>
        <DialogContent data-testid="billing-confirm-modal">
          <DialogHeader>
            <DialogTitle>
              {confirmPlan && PLAN_RANK[confirmPlan.id] < PLAN_RANK[plan] ? "Downgrade" : "Upgrade"} to {confirmPlan?.name}
            </DialogTitle>
            <DialogDescription>
              You will be sent to Stripe Checkout to complete the change. {confirmPlan?.name} is {confirmPlan?.pricing[cycle].price} {confirmPlan?.pricing[cycle].period}, billed through Stripe, and you can cancel anytime from the billing portal.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button data-testid="billing-confirm-cancel-btn" onClick={() => setConfirmPlan(null)} disabled={!!busy}
              className={outlineBtnCls}>
              Cancel
            </button>
            <button data-testid="billing-confirm-continue-btn" onClick={() => upgrade(confirmPlan.pricing[cycle].lookup)} disabled={!!busy}
              className={btnCls}>
              {busy && busy !== "portal" ? "Redirecting..." : "Continue to checkout"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
